"use client"

import { useActionState, useEffect, useMemo, useState } from "react"
import { submitSecurityCheck, type SecurityCheckState } from "@/app/actions/submit-security-check"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import type { LeadCheckConfig } from "@/data/lead-checks"
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  CheckCircle2,
  Loader2,
  ArrowRight,
  ArrowLeft,
  MessageCircle,
  AlertTriangle,
  Clock,
  Gift,
  Lock,
} from "lucide-react"

type AnswerValue = "ja" | "nein" | "unsicher"

const OPTIONS: { value: AnswerValue; label: string }[] = [
  { value: "ja", label: "Ja" },
  { value: "unsicher", label: "Unsicher" },
  { value: "nein", label: "Nein" },
]

function scoreFor(answers: AnswerValue[]) {
  return answers.reduce((sum, a) => sum + (a === "ja" ? 1 : a === "unsicher" ? 0.5 : 0), 0)
}

function levelFor(
  score: number,
  max: number,
  subjectNoun: string
): { label: string; color: string; Icon: typeof ShieldCheck; headline: string; sub: string } {
  const ratio = max > 0 ? score / max : 0
  if (ratio >= 0.83)
    return {
      label: "Solide aufgestellt",
      color: "text-green-600 dark:text-green-400",
      Icon: ShieldCheck,
      headline: `${subjectNoun} ist solide aufgestellt`,
      sub: "Eine gute Basis! Mit ein paar Feinheiten machen wir sie noch widerstandsfähiger.",
    }
  if (ratio >= 0.5)
    return {
      label: "Es gibt Lücken",
      color: "text-amber-500",
      Icon: ShieldAlert,
      headline: "Es gibt spürbare Lücken",
      sub: "Mehrere wichtige Punkte fehlen. Das lässt sich mit überschaubarem Aufwand schließen.",
    }
  return {
    label: "Akuter Handlungsbedarf",
    color: "text-destructive",
    Icon: ShieldX,
    headline: "Akuter Handlungsbedarf",
    sub: `${subjectNoun} ist aktuell angreifbar. Wir sollten zeitnah sprechen, bevor etwas passiert.`,
  }
}

const initialState: SecurityCheckState = {}

export function LeadCheck({ config }: { config: LeadCheckConfig }) {
  const QUESTIONS = config.questions
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<(AnswerValue | null)[]>(Array(QUESTIONS.length).fill(null))
  const [showResult, setShowResult] = useState(false)
  const [formLoadedAt, setFormLoadedAt] = useState("")
  const [state, formAction, isPending] = useActionState(submitSecurityCheck, initialState)

  useEffect(() => {
    setFormLoadedAt(Date.now().toString())
  }, [])

  const answered = answers.filter(Boolean) as AnswerValue[]
  const score = useMemo(() => scoreFor(answered), [answered])
  const level = useMemo(() => levelFor(score, QUESTIONS.length, config.subjectNoun), [score, QUESTIONS.length, config.subjectNoun])
  const risks = useMemo(
    () => QUESTIONS.filter((_, i) => answers[i] === "nein" || answers[i] === "unsicher").map((q) => q.risk),
    [answers, QUESTIONS]
  )
  const progress = Math.round(((showResult ? QUESTIONS.length : step) / QUESTIONS.length) * 100)

  function choose(value: AnswerValue) {
    const next = [...answers]
    next[step] = value
    setAnswers(next)
    setTimeout(() => {
      if (step < QUESTIONS.length - 1) setStep(step + 1)
      else setShowResult(true)
    }, 180)
  }

  const header = (
    <div className="max-w-2xl mx-auto text-center mb-8">
      <div className="inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1 text-sm font-medium text-primary mb-4">
        <ShieldCheck className="h-4 w-4" />
        {config.badge}
      </div>
      <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{config.introTitle}</h2>
      <p className="mt-3 text-muted-foreground">{config.introText}</p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-primary" /> 2 Minuten
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Gift className="h-4 w-4 text-primary" /> 100 % kostenlos
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Lock className="h-4 w-4 text-primary" /> Kein Verkaufsdruck
        </span>
      </div>
    </div>
  )

  // ---- Success state ----
  if (state.success) {
    return (
      <Card className="w-full">
        <CardContent className="flex flex-col items-center justify-center text-center p-8 sm:p-12 animate-in fade-in-50">
          <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
          <h3 className="text-2xl font-bold mb-2">Geschafft – Ihre Auswertung ist unterwegs!</h3>
          <p className="text-muted-foreground max-w-md mb-6">
            Ich schaue mir Ihre Antworten persönlich an und melde mich innerhalb von 24 Stunden mit Ihrer
            individuellen Auswertung und konkreten Empfehlungen. Kostenlos und unverbindlich.
          </p>
          <Button asChild className="bg-[#25D366] hover:bg-[#20BD5A] text-white border-none">
            <a
              href={`https://wa.me/491784912583?text=${encodeURIComponent(config.whatsappText)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Sofort per WhatsApp besprechen
            </a>
          </Button>
        </CardContent>
      </Card>
    )
  }

  // ---- Result + lead form ----
  if (showResult) {
    const { Icon } = level
    return (
      <>
        {header}
        <Card className="w-full overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col items-center text-center mb-8">
              <Icon className={`h-16 w-16 mb-3 ${level.color}`} />
              <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Ihr Ergebnis</div>
              <h3 className={`text-2xl sm:text-3xl font-bold mt-1 ${level.color}`}>{level.headline}</h3>
              <p className="text-muted-foreground mt-2 max-w-md">{level.sub}</p>
              <div className="mt-4 inline-flex items-baseline gap-1 rounded-full border bg-muted/40 px-4 py-2">
                <span className="text-2xl font-bold">{score % 1 === 0 ? score : score.toFixed(1)}</span>
                <span className="text-muted-foreground">/ {QUESTIONS.length} Punkten</span>
              </div>
            </div>

            {risks.length > 0 && (
              <div className="mb-8 rounded-xl border border-destructive/20 bg-destructive/5 p-5">
                <h4 className="flex items-center gap-2 font-semibold mb-3">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Ihre {risks.length} größten Risiken
                </h4>
                <ul className="space-y-2">
                  {risks.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-xl bg-primary/5 border border-primary/20 p-5 sm:p-6">
              <h4 className="font-semibold text-lg mb-1">Ihre persönliche Auswertung – kostenlos</h4>
              <p className="text-sm text-muted-foreground mb-5">
                Ich sende Ihnen eine konkrete Schritt-für-Schritt-Empfehlung, wie Sie Ihre Lücken schließen. Kein
                Verkaufsgespräch, kein Risiko.
              </p>
              <form action={formAction} className="space-y-4">
                {/* Honeypot */}
                <div className="absolute -left-[9999px]" aria-hidden="true" tabIndex={-1}>
                  <label htmlFor="lc-website">Website</label>
                  <input type="text" id="lc-website" name="website" tabIndex={-1} autoComplete="off" />
                </div>
                <input type="hidden" name="formLoadedAt" value={formLoadedAt} />
                <input type="hidden" name="source" value={config.source} />
                <input type="hidden" name="score" value={score % 1 === 0 ? String(score) : score.toFixed(1)} />
                <input type="hidden" name="scoreMax" value={QUESTIONS.length} />
                <input type="hidden" name="level" value={level.label} />
                <input
                  type="hidden"
                  name="answers"
                  value={JSON.stringify(QUESTIONS.map((q, i) => ({ question: q.question, answer: answers[i] ?? "-" })))}
                />
                <input type="hidden" name="risks" value={JSON.stringify(risks)} />

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lc-name">Name / Unternehmen</Label>
                    <Input id="lc-name" name="name" placeholder="Max Mustermann" required />
                    {state.errors?.name && <p className="text-sm text-red-500">{state.errors.name.join(", ")}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lc-email">E-Mail-Adresse</Label>
                    <Input id="lc-email" name="email" type="email" placeholder="kontakt@beispiel.de" required />
                    {state.errors?.email && <p className="text-sm text-red-500">{state.errors.email.join(", ")}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lc-phone">Telefon (optional, für schnellere Rückmeldung)</Label>
                  <Input id="lc-phone" name="phone" type="tel" placeholder="0151 12345678" />
                </div>

                {state.error && (
                  <div className="p-3 rounded-md bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
                    {state.error}
                  </div>
                )}

                <Button type="submit" size="lg" className="w-full" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      Kostenlose Auswertung anfordern
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Ihre Daten werden ausschließlich für die Auswertung genutzt und nicht weitergegeben.
                </p>
              </form>
            </div>
          </CardContent>
        </Card>
      </>
    )
  }

  // ---- Quiz ----
  const current = QUESTIONS[step]
  return (
    <>
      {header}
      <Card className="w-full">
        <CardContent className="p-6 sm:p-8">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>
                Frage {step + 1} von {QUESTIONS.length}
              </span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <h3 className="text-xl sm:text-2xl font-semibold mb-6 min-h-[3.5rem]">{current.question}</h3>

          <div className="grid gap-3">
            {OPTIONS.map((opt) => {
              const active = answers[step] === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => choose(opt.value)}
                  className={`flex items-center justify-between rounded-xl border-2 px-5 py-4 text-left font-medium transition-all hover:border-primary hover:bg-primary/5 ${
                    active ? "border-primary bg-primary/5" : "border-border"
                  }`}
                >
                  <span>{opt.label}</span>
                  <ArrowRight
                    className={`h-4 w-4 transition-opacity ${active ? "opacity-100 text-primary" : "opacity-0"}`}
                  />
                </button>
              )
            })}
          </div>

          {step > 0 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="mt-6 inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Zurück
            </button>
          )}
        </CardContent>
      </Card>
    </>
  )
}
